import * as React from "react";
import { Box, Typography, Button, Divider, Grid, InputAdornment, IconButton } from "@mui/material";
import { Image } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import NewCard from "../../app/components/NewCard";
import { main_button } from "../../app/components/Styles";
import { connect } from "react-redux";
import { getExchangeRequestById, approveExchangeRequest, rejectExchangeRequest } from "../../app/store/actions/dataActions";
import { useEffect } from "react";
import moment from "moment";
import { ReasonModal, RefundModal } from "../../app/components";
import { useTranslation } from "react-i18next";

const ExchangeRequest = ({ getExchangeRequestById, approveExchangeRequest, rejectExchangeRequest, exchangeRequest, user, isLoading }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isFincart = user?.vendor?.shippingService === "Fincart";

  useEffect(() => {
    getExchangeRequestById({ exchangeRequestId: id });
  }, [id]);

  const [refundOpen, setRefundOpen] = React.useState(false);
  const handleApprove = () => setRefundOpen(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const handleConfirmRefund = async ({ refundType, refundAmount }) => {
    try {
      setIsSubmitting(true);
      const amountToSend = refundType === 'refund' ? -Math.abs(parseFloat(refundAmount || 0)) : Math.abs(parseFloat(refundAmount || 0));
      await approveExchangeRequest({ exchangeRequestId: id, refundAmount: amountToSend });
      setRefundOpen(false);
      await getExchangeRequestById({ exchangeRequestId: id });
    } finally {
      setIsSubmitting(false);
    }
  };

  const [reasonOpen, setReasonOpen] = React.useState(false);
  const handleReject = () => setReasonOpen(true);
  const handleConfirmReject = async (reason) => {
    try {
      setIsSubmitting(true);
      await rejectExchangeRequest({ exchangeRequestId: id, reason });
      setReasonOpen(false);
      await getExchangeRequestById({ exchangeRequestId: id });
    } finally {
      setIsSubmitting(false);
    }
  };

  const data = exchangeRequest;
  const statusLabels = {
    Pending: t("returnsSystem.status.pending"),
    Accepted: t("returnsSystem.status.accepted"),
    Rejected: t("returnsSystem.status.rejected"),
  };

  const getIncomingUnitPrice = (it) => {
    if (it?.orderItem?.price != null) {
      return parseFloat(it.orderItem.price) - (parseFloat(it.orderItem.discount) || 0);
    }
    return parseFloat(it?.product?.price) || 0;
  };

  const incomingTotal = (data?.incomingItems || []).reduce((sum, it) => {
    return sum + (getIncomingUnitPrice(it) * (parseInt(it?.quantity, 10) || 0));
  }, 0);
  const outgoingTotal = (data?.outgoingItems || []).reduce((sum, it) => {
    return sum + ((parseFloat(it?.product?.price) || 0) * (parseInt(it?.quantity, 10) || 0));
  }, 0);
  const netAmount = incomingTotal - outgoingTotal;
  const calculatedRefundAmount = Math.abs(netAmount);
  const calculatedRefundType = isFincart
    ? "collection"
    : (netAmount >= 0 ? "refund" : "collection");

  return (
    <Box sx={{ width: "100%", paddingX: 5, py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography fontWeight={"bold"} fontSize={20}>{t("returnsSystem.exchangeRequest.title")}</Typography>
        <Button sx={{ ...main_button }} onClick={() => navigate(-1)}>{t("common.back")}</Button>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <NewCard content={<>
            <Typography>{t("returnsSystem.exchangeRequest.request")} {id}</Typography>
            {data?.originalOrder?.shopifyOrderNumber && <Typography>{t("returnsSystem.exchangeRequest.shopify")}: {data?.originalOrder?.shopifyOrderNumber}</Typography>}
            <Typography>{t("returnsSystem.exchangeRequest.createdAt")} {data?.createdAt ? moment(data?.createdAt).format("lll") : '-'}</Typography>
            <Typography fontWeight="bold">{t("common.status")}: {statusLabels[data?.status] || data?.status}</Typography>
            <Typography fontWeight="bold">{t("common.reason")}: {data?.reason}</Typography>
            {data?.rejectedAt && <Typography>{t("returnsSystem.exchangeRequest.rejectedAt")} {moment(data?.rejectedAt).format("lll")}</Typography>}
            {data?.rejectedReason && <Typography>{t("returnsSystem.exchangeRequest.rejectedReason")}: {data?.rejectedReason}</Typography>}
          </>} />
        </Grid>
        <Grid item xs={12} md={8}>
          <NewCard content={<>
            <Typography variant="h6" textAlign="center">{t("returnsSystem.exchangeRequest.originalOrderTitle")}</Typography>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Typography>{t("returnsSystem.exchangeRequest.order")}: {data?.originalOrder?._id}</Typography>
              <Typography>{t("common.status")}: {data?.originalOrder?.status}</Typography>
              <Typography>{t("returnsSystem.exchangeRequest.deliveredAt")}: {data?.originalOrder?.deliveredAt ? moment(data?.originalOrder?.deliveredAt).format('lll') : '-'}</Typography>
            </Box>
          </>} />

          <NewCard sx={{ mt: 2 }} content={<>
            <Typography variant="h6" textAlign="center">{t("returnsSystem.exchangeRequest.incomingItemsTitle")}</Typography>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={5} md={5}>
                  <Typography fontWeight={"bold"}>{t("returnsSystem.exchangeRequest.productName")}</Typography>
                </Grid>
                <Grid item xs={3} md={3}>
                  <Typography fontWeight={"bold"}>{t("common.sku")}</Typography>
                </Grid>
                <Grid item xs={2} md={2}>
                  <Typography fontWeight={"bold"}>{t("common.quantity")}</Typography>
                </Grid>
                <Grid item xs={2} md={2}>
                  <Typography fontWeight={"bold"}>{t("common.price")}</Typography>
                </Grid>
                {data?.incomingItems?.map((it, idx) => (
                  <React.Fragment key={idx}>
                    <Grid item xs={5} md={5}>
                      <Typography>{it?.product?.name}</Typography>
                    </Grid>
                    <Grid item xs={3} md={3}>
                      <Typography>{it?.product?.sku || it?.product?.externalId || '-'}</Typography>
                    </Grid>
                    <Grid item xs={2} md={2}>
                      <Typography>{it?.quantity}</Typography>
                    </Grid>
                    <Grid item xs={2} md={2}>
                      <Typography>{it?.orderItem?.price ? (it?.orderItem?.price - (it?.orderItem?.discount || 0)) : it?.product?.price} EGP</Typography>
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Box>
          </>} />

          <NewCard sx={{ mt: 2 }} content={<>
            <Typography variant="h6" textAlign="center">{t("returnsSystem.exchangeRequest.outgoingItemsTitle")}</Typography>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={5} md={5}>
                  <Typography fontWeight={"bold"}>{t("returnsSystem.exchangeRequest.productName")}</Typography>
                </Grid>
                <Grid item xs={3} md={3}>
                  <Typography fontWeight={"bold"}>{t("common.sku")}</Typography>
                </Grid>
                <Grid item xs={2} md={2}>
                  <Typography fontWeight={"bold"}>{t("common.quantity")}</Typography>
                </Grid>
                <Grid item xs={2} md={2}> 
                  <Typography fontWeight={"bold"}>{t("common.price")}</Typography>
                </Grid>
                {data?.outgoingItems?.map((it, idx) => (
                  <React.Fragment key={idx}>
                    <Grid item xs={5} md={5}>
                      <Typography>{it?.product?.name}</Typography>
                    </Grid>
                    <Grid item xs={3} md={3}>
                      <Typography>{it?.product?.sku || it?.product?.externalId || '-'}</Typography>
                    </Grid>
                    <Grid item xs={2} md={2}>
                      <Typography>{it?.quantity}</Typography>
                    </Grid>
                    <Grid item xs={2} md={2}>
                      <Typography>{it?.product?.price} EGP</Typography>
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Box>
          </>} />

          <NewCard sx={{ mt: 2 }} content={<>
            <Typography variant="h6" textAlign="center">{t("returnsSystem.exchangeRequest.customerAdditionalInfoTitle")}</Typography>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Typography>{data?.additionalInfo || t("returnsSystem.exchangeRequest.noAdditionalInfo")}</Typography>
            </Box>
          </>} />

          <NewCard sx={{ mt: 2 }} content={<>
            <Typography variant="h6" textAlign="center">{t("returnsSystem.exchangeRequest.attachmentsTitle")}</Typography>
            <Divider />
            <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {data?.attachments && data.attachments.length > 0 ? (
                <Image.PreviewGroup>
                  {data.attachments.map((url, idx) => (
                    <Image
                      key={idx}
                      src={url}
                      alt={`attachment-${idx}`}
                      style={{ borderRadius: 4 }}
                      height={120}
                      width={160}
                    />
                  ))}
                </Image.PreviewGroup>
              ) : (
                <Typography>{t("returnsSystem.exchangeRequest.noAttachments")}</Typography>
              )}
            </Box>
          </>} />

          {data?.status === 'Pending' && (
            <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button sx={{ ...main_button }} onClick={handleApprove} disabled={isSubmitting}>{t("returnsSystem.exchangeRequest.approve")}</Button>
              <Button sx={{ ...main_button }} onClick={handleReject} disabled={isSubmitting}>{t("returnsSystem.exchangeRequest.reject")}</Button>
            </Box>
          )}
        </Grid>
      </Grid>
      <ReasonModal
        open={reasonOpen}
        onClose={() => !isSubmitting && setReasonOpen(false)}
        onConfirm={handleConfirmReject}
        title={t("returnsSystem.exchangeRequest.rejectTitle")}
        message={t("returnsSystem.exchangeRequest.rejectMessage")}
      />
      <RefundModal
        open={refundOpen}
        onClose={() => !isSubmitting && setRefundOpen(false)}
        onConfirm={handleConfirmRefund}
        title={t("returnsSystem.exchangeRequest.approveTitle")}
        message={t("returnsSystem.exchangeRequest.approveMessage")}
        showType={true}
        allowCashRefund={!isFincart}
        initialRefundType={calculatedRefundType}
        initialRefundAmount={calculatedRefundAmount}
        isLoading={isSubmitting}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  exchangeRequest: state?.records?.exchangeRequest,
  user: state?.auth?.user,
  isLoading: state?.records?.isLoading,
});

const mapDispatchToProps = { getExchangeRequestById, approveExchangeRequest, rejectExchangeRequest };

export default connect(mapStateToProps, mapDispatchToProps)(ExchangeRequest);

